import {
  Field,
  Input,
  InputGroup,
  createRecipeContext,
  HTMLChakraProps,
  RecipeVariantProps,
  CloseButton,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import { ReactElement, memo, forwardRef } from 'react';
import { searchInputRecipe } from './searchInput.recipe';

const { withContext } = createRecipeContext({
  recipe: searchInputRecipe,
});

interface SearchInputProps extends HTMLChakraProps<
  'input',
  RecipeVariantProps<typeof searchInputRecipe>
> {
  label?: string;
  name: string;
  error?: string | null;
  placeholder?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  value?: string;
  startElement?: ReactElement;
  e2eTitle?: string;
  handleClose?: () => void;
  showClose?: boolean;
}

const DefaultSearchIcon = memo(() => <FaSearch size="16px" />);

/**
 * @useFor a standalone search input — magnifying-glass icon prefix,
 *   optional close button (clears value), label + error state via
 *   `<Field.Root>`. Used outside table toolbars.
 *
 *   Examples in-tree: ONE landing search, global search bar in
 *   admin nav, drawer-internal "find existing" pickers.
 *
 *   For search input INSIDE a `<TableToolbar>` use `<SearchBar>`
 *   instead (different visual weight, designed to slot inside the
 *   toolbar's pill). For autocomplete / multi-select search use
 *   `<SelectMulti>` (searchable combobox).
 */
const SearchInputInner = forwardRef<HTMLInputElement, SearchInputProps & { visual?: string }>(
  (
    {
      label,
      name,
      error,
      placeholder,
      onChange,
      value,
      startElement,
      e2eTitle,
      visual,
      handleClose,
      showClose,
      ...inputProps
    },
    ref
  ) => {
    const iconColor =
      visual === 'flat' || visual === 'outline'
        ? 'text.primary !important'
        : 'text.secondary';
    const endElement = showClose ? (
      <CloseButton size="xs" onClick={handleClose} me="-2" />
    ) : undefined;
    return (
      <Field.Root>
        {label && <Field.Label htmlFor={name}>{label}</Field.Label>}
        <InputGroup
          startElement={startElement || <DefaultSearchIcon />}
          startElementProps={{ color: iconColor }}
          endElement={endElement}
        >
          <Input
            ref={ref}
            name={name}
            type="search"
            placeholder={placeholder}
            onChange={onChange}
            value={value}
            color={'text.primary'}
            data-e2e={e2eTitle}
            border={'1px solid'}
            borderColor={'border.card'}
            bg={'surface.searchInput'}
            {...inputProps}
          />
        </InputGroup>
        {error && <Field.ErrorText>{error}</Field.ErrorText>}
      </Field.Root>
    );
  }
);

SearchInputInner.displayName = 'SearchInputInner';

const SearchInput = withContext<HTMLInputElement, SearchInputProps>(SearchInputInner);

export { SearchInput };
